/**
 * Quiz Score Service - Manages quiz scores for users and articles
 * Stores scores locally in localStorage and syncs with database if available
 */

import type { QuizScore } from '@/types';
import { supabase } from '@/config/supabase';

const STORAGE_KEY = 'nanolez_quiz_scores';

/**
 * Generate a UUID v4
 */
const generateUUID = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Save a quiz score locally and to database
 */
export const saveQuizScore = async (
  userId: string,
  articleId: string,
  articleTitle: string,
  score: number,
  totalQuestions: number,
  correctAnswers: number,
  userAnswers: number[],
  passingScore: number = 70
): Promise<QuizScore> => {
  const quizScore: QuizScore = {
    id: generateUUID(),
    userId,
    articleId,
    articleTitle,
    score,
    totalQuestions,
    correctAnswers,
    userAnswers,
    completedAt: new Date().toISOString(),
    isPassed: score >= passingScore
  };

  try {
    // Save to localStorage
    const scores = getLocalScores();
    scores.push(quizScore);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(scores));
    console.log('✅ Quiz score saved to localStorage:', quizScore);

    // Try to sync to database
    try {
      await syncScoreToDB(quizScore);
    } catch (dbError) {
      console.warn('⚠️  Failed to sync quiz score to database (will retry later):', dbError);
      // Score is still saved locally, so continue
    }
  } catch (error) {
    console.error('❌ Error saving quiz score:', error);
  }

  return quizScore;
};

/**
 * Get all quiz scores for a user from localStorage
 */
export const getUserScores = (userId: string): QuizScore[] => {
  try {
    const scores = getLocalScores();
    return scores.filter(s => s.userId === userId);
  } catch (error) {
    console.error('❌ Error retrieving user scores:', error);
    return [];
  }
};

/**
 * Get quiz scores for a specific article
 */
export const getArticleScores = (articleId: string, userId?: string): QuizScore[] => {
  try {
    const scores = getLocalScores();
    return scores.filter(s => 
      s.articleId === articleId && 
      (!userId || s.userId === userId)
    );
  } catch (error) {
    console.error('❌ Error retrieving article scores:', error);
    return [];
  }
};

/**
 * Get the best score for a user on a specific article
 */
export const getBestScore = (userId: string, articleId: string): QuizScore | null => {
  const scores = getUserScores(userId).filter(s => s.articleId === articleId);
  if (scores.length === 0) return null;
  return scores.reduce((best, current) => 
    current.score > best.score ? current : best
  );
};

/**
 * Get quiz statistics for a user
 */
export const getUserQuizStats = (userId: string) => {
  const scores = getUserScores(userId);
  const totalAttempts = scores.length;
  const passedAttempts = scores.filter(s => s.isPassed).length;
  const averageScore = totalAttempts > 0 
    ? Math.round(scores.reduce((sum, s) => sum + s.score, 0) / totalAttempts)
    : 0;

  return {
    totalAttempts,
    passedAttempts,
    averageScore,
    passRate: totalAttempts > 0 
      ? Math.round((passedAttempts / totalAttempts) * 100)
      : 0
  };
};

/**
 * Get local scores from localStorage
 */
const getLocalScores = (): QuizScore[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    console.warn('⚠️  Failed to parse quiz scores from localStorage');
    return [];
  }
};

/**
 * Sync a quiz score to Supabase database
 */
const syncScoreToDB = async (quizScore: QuizScore): Promise<void> => {
  try {
    const { data, error } = await supabase
      .from('quiz_scores')
      .insert([{
        id: quizScore.id,
        user_id: quizScore.userId,
        article_id: quizScore.articleId,
        article_title: quizScore.articleTitle,
        score: quizScore.score,
        total_questions: quizScore.totalQuestions,
        correct_answers: quizScore.correctAnswers,
        user_answers: JSON.stringify(quizScore.userAnswers),
        completed_at: quizScore.completedAt,
        is_passed: quizScore.isPassed
      }]);

    if (error) {
      console.error('❌ Database error syncing quiz score:', error);
      throw error;
    }

    console.log('✅ Quiz score synced to database');
  } catch (error) {
    console.error('❌ Error syncing quiz score to database:', error);
    // Silently fail - score is already saved locally
  }
};

/**
 * Clear all quiz scores for a user (careful!)
 */
export const clearUserScores = (userId: string): void => {
  try {
    const scores = getLocalScores();
    const filtered = scores.filter(s => s.userId !== userId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    console.log('✅ Cleared quiz scores for user:', userId);
  } catch (error) {
    console.error('❌ Error clearing quiz scores:', error);
  }
};

/**
 * Export scores as CSV for analytics
 */
export const exportScoresAsCSV = (userId: string): string => {
  const scores = getUserScores(userId);
  const headers = ['Article', 'Score', 'Date', 'Passed'];
  const rows = scores.map(s => [
    s.articleTitle,
    `${s.score}%`,
    new Date(s.completedAt).toLocaleDateString(),
    s.isPassed ? 'Yes' : 'No'
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');

  return csvContent;
};
